/**
 * Account Entity - Domain Types
 *
 * Read-mostly representation of Dynamics CRM Accounts. The Accounts table is
 * the system of record; the few Vera-side writes (owner, Broker of Record,
 * term date, address) go through BFF endpoints with the service identity,
 * never through this entity. Used primarily for Groups (customertypecode
 * filtering).
 */

// =============================================================================
// Core Types
// =============================================================================

export interface AccountAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface Account {
  id: string;
  name: string;
  accountNumber?: string;
  customerTypeCode?: number;
  customerTypeName?: string;
  stateCode?: number;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  /**
   * Full Address 1 / Address 2 slots. Populated by accountApi.get (detail
   * fetch) only; list queries keep a lean select and leave these undefined.
   * Undefined also when every component is blank in CRM.
   */
  address1?: AccountAddress;
  address2?: AccountAddress;
  /** Broker of Record (lookup → contact). dts_brokerofrecord on account. */
  brokerOfRecordContactId?: string;
  brokerOfRecordName?: string;
}

// =============================================================================
// Customer Type Option Set (fetched dynamically from Dataverse metadata)
// =============================================================================

export interface CustomerTypeOption {
  value: number;
  label: string;
}

/**
 * Labels that identify "group" account types.
 * These labels are consistent across environments even when integer codes differ.
 */
export const GROUP_TYPE_LABELS = ['Group', 'Prospect Group'] as const;

// =============================================================================
// Query Types
// =============================================================================

export interface AccountQueryParams {
  /** Search by name (contains) */
  nameFilter?: string;
  /** Filter to specific customer type codes */
  customerTypeCodes?: number[];
  /** Only active accounts */
  activeOnly?: boolean;
  /** Max results */
  top?: number;
}
